import React, {useState, useEffect, useCallback, forwardRef} from 'react'
import {Stack, Box, Text, Label} from '@sanity/ui'
import {set, unset, useFormValue} from 'sanity'
import {useClient} from 'sanity'
import {ObjectInputProps, ReferenceSchemaType} from 'sanity'
import useFileName from './useFileName'

interface ShowFilenameValue {
  _ref?: string
  _type?: 'reference'
}

interface ShowFilenameProps extends Omit<ObjectInputProps, 'value'> {
  value?: ShowFilenameValue
  inputName?: string
  parentValue?: {
    asset?: {
      _ref: string
    }
  }
}

interface DocumentOption {
  _id: string
  name: string
}

const ShowFilename = forwardRef<HTMLDivElement, ShowFilenameProps>((props, ref) => {
  const {value, onChange, schemaType, inputName, path} = props
  const client = useClient()

  const parentValue = useFormValue(path.slice(0, -1)) as any

  const [values, setValues] = useState<DocumentOption[]>([])
  const fileName = useFileName(parentValue?.asset?._ref)

  // Get document type from schema - cast to ReferenceSchemaType to access 'to' property
  const referenceSchemaType = schemaType as ReferenceSchemaType
  const documentType = referenceSchemaType?.to?.[0]?.name || ''

  // Fetch available reference options
  useEffect(() => {
    if (!documentType) return

    client
      .fetch(`*[_type == $documentType]{ _id, name }`)
      .then(setValues)
      .catch((error) => {
        console.error('Error fetching reference options:', error)
        setValues([])
      })
  }, [documentType, client])

  // Handle value changes
  const handleValueChange = useCallback(
    (newValue: string | null) => {
      if (!newValue) {
        onChange(unset())
        return
      }

      const matchedValue = values.find(
        (option) => option.name.toLowerCase() === newValue.toLowerCase(),
      )

      if (matchedValue) {
        onChange(set({_ref: matchedValue._id, _type: 'reference'}))
      }
    },
    [onChange, values],
  )

  // Auto-populate from filename
  useEffect(() => {
    if (!fileName || value?._ref || !inputName) return

    try {
      const fileNameWithoutExtension = fileName.split('.')[0]
      const params = new URLSearchParams(fileNameWithoutExtension)
      const extractedValue = params.get(inputName)

      if (extractedValue) {
        handleValueChange(extractedValue)
      }
    } catch (error) {
      console.error('Error parsing filename:', error)
    }
  }, [fileName, value, inputName, handleValueChange])

  // Find current selection name
  const currentName = values.find((option) => option._id === value?._ref)?.name || ''

  return (
    <Stack gap={3} ref={ref}>
      <Box>
        <Label size={1} weight="medium">
          {schemaType.title || 'File Reference'}
        </Label>
        {schemaType.description && (
          <Text size={1} muted style={{marginTop: 4}}>
            {schemaType.description}
          </Text>
        )}
      </Box>

      <Stack gap={2}>
        {fileName && (
          <Box
            padding={2}
            style={{
              backgroundColor: 'var(--card-bg-color)',
              border: '1px solid var(--card-border-color)',
              borderRadius: 4,
              fontSize: 12,
            }}
          >
            <Text size={1} muted>
              Filename: {fileName}
            </Text>
          </Box>
        )}

        {currentName && (
          <Box>
            <Text size={1}>Selected: {currentName}</Text>
          </Box>
        )}

        {!value?._ref && !fileName && (
          <Text size={1} muted>
            No file selected or reference set
          </Text>
        )}
      </Stack>
    </Stack>
  )
})

ShowFilename.displayName = 'ShowFilename'

export default ShowFilename
